import {Injectable} from '@angular/core';
import {BehaviorSubject, first, firstValueFrom, merge} from 'rxjs';
import {FloatingActionButton, FloatingActionButtonService} from '@mydocma/component-library/src/lib/floating-action-button';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AppContainerService, AppRightDrawerService, AppRightPaneService} from '@mydocma/app-container';
import {MmSideNavBarService} from '../../../mm-sidenavbar/mm-sidenavbar.service';
import {MmFaultRightPaneService} from '../mm-fault-right-pane/mm-fault-right-pane.service';
import {MmFaultRightPanePageEnum} from '../mm-fault-right-pane/mm-fault-right-pane.enum';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {Router} from '@angular/router';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {HeaderToggleMenuEnum, MmFaultsSplitService} from './mm-faults-split.service';
import {MmPlansManagerService} from '../../mm-plans-view/services/mm-plans-manager.service';
import {MmPlansFilterService} from '../../mm-plans-view/services/mm-plans-filter.service';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {distinctUntilChanged} from 'rxjs/operators';
import {MmPlanMobileViewService} from '../../mm-plans-view/services/mm-plan-mobile-view.service';
import { MmRoutingService } from '../../../mm-routing/mm-routing.service';

export type id = string;
export type tabIndex = number;

export enum FloatButtonsTypesEnum {
  activities = 'activities',
  faultsView = 'faultsView',
  attachments = 'attachments',
  planView = 'planView'
}

@UntilDestroy()
@Injectable()
export class MmFaultsFabButtonService {

  private currentSelectedTabIndexSubject = new BehaviorSubject<tabIndex>(null);
  public currentSelectedTabIndex$ = this.currentSelectedTabIndexSubject.asObservable();
  private activeFabXsButtonsSubject = new BehaviorSubject<Map<id, FloatingActionButton>>(new Map());

  constructor(private appRightPaneService: AppRightPaneService,
              private fabService: FloatingActionButtonService,
              private appContainerService: AppContainerService,
              private mmSideNavBarService: MmSideNavBarService,
              private mmFaultRightPaneService: MmFaultRightPaneService,
              private readonly selectionService: SelectionService<ViewFault>,
              private mmProjectService: MmProjectService,
              private plansFilterService: MmPlansFilterService,
              private plansManagerService: MmPlansManagerService,
              private mmFaultsSplitService: MmFaultsSplitService,
              private mobilePlanViewService: MmPlanMobileViewService,
              private readonly router: Router,
              private readonly planEditService: MmPlanEditService,
              private readonly appRightDrawerService: AppRightDrawerService,
              private readonly mmRoutingService: MmRoutingService) {

    merge(
      this.plansManagerService.showPlanFlyout$.pipe(distinctUntilChanged()),
      this.selectionService.selectionMode.pipe(distinctUntilChanged()),
      this.appContainerService.isLeftPaneOpen.pipe(distinctUntilChanged()),
      //user can select already selected plan, in thatc ase we still need to emit value in order to show fab button
      this.plansManagerService.selectedPlan$,
      this.planEditService.planEditMode$.pipe(distinctUntilChanged())
    ).pipe(untilDestroyed(this))
      .subscribe(value => {
        this.checkFabVisibility()
      })

    this.mmProjectService.currentProject.subscribe(value => {
      this.activeFabXsButtonsSubject.value.clear();
      this.unregisterButton()
    })

    this.mmFaultRightPaneService.currentPage$
      .pipe(untilDestroyed(this))
      .subscribe(async page => {
        if (page === MmFaultRightPanePageEnum.fault) {
          this.checkFabVisibility()
        } else {
          this.unregisterButton();
        }
      });

    this.mmRoutingService.moduleActivatedRoute$
    .pipe(untilDestroyed(this))
    .subscribe(() => this.checkFabVisibility());

    this.appRightPaneService.isOpen
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(async isOpen => this.checkFabVisibility());

    this.appRightDrawerService.isOpen
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(async isOpen => this.checkFabVisibility());
  }

  get currentRightPaneTabIndex() {
    return this.currentSelectedTabIndexSubject.value;
  }

  set currentRightPaneTabIndex(tbIndex: tabIndex) {
    this.currentSelectedTabIndexSubject.next(tbIndex);
    this.checkFabVisibility();
  }

  get activeFabXsButtons(): Map<id, FloatingActionButton> {
    return this.activeFabXsButtonsSubject.value;
  }

  public registerButton(button: FloatingActionButton): void {
    this.activeFabXsButtonsSubject.value.set(button.id, button);
    this.activeFabXsButtonsSubject.next(this.activeFabXsButtonsSubject.value);
    this.checkFabVisibility();
  }

//depending on certain conditions in app show or hide fab button
  // todo  logic of unregistering  button can be set in same method to reduce (if) condition check for better readability
  private async checkFabVisibility(selectionMode?: boolean): Promise<void> {
    const isRightPaneOpen = this.appRightPaneService?.opened;
    const isAppRightDrawerOpen = await firstValueFrom(this.appRightDrawerService?.isOpen);

    if (await this.isFabNotAllowed(selectionMode) || !!isAppRightDrawerOpen) {
      this.unregisterButton();
      return;
    }
    if (isRightPaneOpen) {
      this.handleRightPaneOpen()
      return;
    }
    // is plan view
    // or mobile
    // or plan selected
    // we dont need to check if list is visible we do this in checkFabButtonVisible

    const isPlanViewConditionsMet = this.mmFaultsSplitService.toggledRightViews?.[0] === HeaderToggleMenuEnum.planView
      && this.appContainerService.isMobileView
      && this.plansManagerService.selectedPlan;

    if (isPlanViewConditionsMet) {
      this.showFab(FloatButtonsTypesEnum.planView);
      return
    }

    if (this.router.url.includes('/faults')) {
      this.showFab(FloatButtonsTypesEnum.faultsView)
    } else {
      this.unregisterButton();
    }
  }

  // depending on which tab is open hide (if index is 0, or fist tab) or show fab with button assigned int ts component
  private handleRightPaneOpen(): void {
    if (this.mmFaultRightPaneService.currentPage !== MmFaultRightPanePageEnum.fault) {
      this.unregisterButton();
      return;
    }
    if (this.currentRightPaneTabIndex === 1) {
      this.showFab(FloatButtonsTypesEnum.activities);
    } else if (this.currentRightPaneTabIndex === 2) {
      this.showFab(FloatButtonsTypesEnum.attachments);
    } else {
      this.unregisterButton();
    }
  }


  private showFab(id: string) {
    const fab = this.activeFabXsButtons.get(id);
    if (fab) {
      this.fabService.registerXsFabButton(fab);
    }
  }

  public unregisterButton(): void {
    this.fabService.unregisterXsFabButton();
  }

  private async isFabNotAllowed(selectionMode): Promise<boolean> {
    const _selectionMode = (!selectionMode ? await firstValueFrom(this.selectionService.selectionMode.pipe(first())) : selectionMode);

    const isLeftPaneOpen = this.mmSideNavBarService?.isleftPaneOpened;
    const isPlanFlyoutOpen = (await firstValueFrom(this.plansManagerService.showPlanFlyout$)) || !this.plansManagerService.selectedPlan;
    const mobilePinCreationMode = this.mobilePlanViewService.fullScreen;
    const isDVPaneOpen = this.appRightPaneService?.opened &&  this.mmFaultRightPaneService.currentPage === MmFaultRightPanePageEnum.fault;

    return isLeftPaneOpen
      || (_selectionMode && this.appContainerService.isMobileView)

      // if plan view is opened
      // mobile view
      // selected plan or planLayoutVisible
      || this.checkPlanViewConditions(isPlanFlyoutOpen, mobilePinCreationMode, isDVPaneOpen);
  }

  private checkPlanViewConditions(isPlanFlyoutOpen: boolean, mobilePinCreationMode: boolean, isDVPaneOpen: boolean): boolean {
    return this.mmFaultsSplitService.toggledRightViews?.[0] === HeaderToggleMenuEnum.planView
      && this.appContainerService.isMobileView
      && (isPlanFlyoutOpen || mobilePinCreationMode) && !isDVPaneOpen;
  }
}

